// components/AnalysisControls.tsx
import React from 'react';
import Select, { MultiValue, ActionMeta } from 'react-select';
import { BuildingType } from '../../../types/BuildingTypeEnums';
import {
    DateRange,
    TimeGrouping,
    BuildingTypeOption,
    UserGroup
} from '../types/analysis.types';
import TimeGroupingControl from './TimeGroupingControl';
import { BuildingOption } from '../utils/buildingFilter';

interface AnalysisControlsProps {
    buildingTypeOptions: BuildingTypeOption[];
    selectedBuildingTypeOptions: BuildingTypeOption[];
    onBuildingTypeChange: (options: BuildingTypeOption[]) => void;
    selectedBuildingTypes: BuildingType[];
    onSelectedBuildingTypesChange: (types: BuildingType[]) => void;
    selectedUserGroup: string | null;
    onUserGroupChange: (groupKey: string | null) => void;
    userGroups: UserGroup[];
    timeGrouping: TimeGrouping;
    onTimeGroupingChange: (grouping: TimeGrouping) => void;
    dateRange: DateRange;
    onDateRangeChange: (range: DateRange) => void;
    /** The buildings that can be picked (those matching the type and company filters) */
    buildingOptions: BuildingOption[];
    /** The picked buildings; empty = all buildings. One the other filters exclude shows greyed */
    selectedBuildings: BuildingOption[];
    onSelectedBuildingKeysChange: (keys: string[]) => void;
    /** Why some picked buildings count for nothing, shown under the field */
    buildingNote?: string | null;
    /** Tidsperiod here; Jämför år shows it next to its chart instead */
    showTimeGrouping?: boolean;
    /** Från/Till; Jämför år always compares whole years */
    showDateRange?: boolean;
    /** More controls at the end of the row (Jämför år's År picker) */
    extraControls?: React.ReactNode;
}

const AnalysisControls: React.FC<AnalysisControlsProps> = ({
                                                               buildingTypeOptions,
                                                               selectedBuildingTypeOptions,
                                                               onBuildingTypeChange,
                                                               selectedBuildingTypes,
                                                               onSelectedBuildingTypesChange,
                                                               selectedUserGroup,
                                                               onUserGroupChange,
                                                               userGroups,
                                                               timeGrouping,
                                                               onTimeGroupingChange,
                                                               dateRange,
                                                               onDateRangeChange,
                                                               buildingOptions,
                                                               selectedBuildings,
                                                               onSelectedBuildingKeysChange,
                                                               buildingNote,
                                                               showTimeGrouping = true,
                                                               showDateRange = true,
                                                               extraControls
                                                           }) => {
    const handleBuildingTypeChange = (
        newValue: MultiValue<BuildingTypeOption>,
        actionMeta: ActionMeta<BuildingTypeOption>
    ): void => {
        const selectedOptions = Array.from(newValue);
        const selectedTypes = selectedOptions.map(option => option.value);
        onSelectedBuildingTypesChange(selectedTypes);
        onBuildingTypeChange(selectedOptions);
    };

    const handleUserGroupChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        onUserGroupChange(value === "all" ? null : value);
    };

    const handleDateRangeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = event.target;
        onDateRangeChange({
            ...dateRange,
            [name]: value
        });
    };

    return (
        <div className="controls-container">
            <div className="control-group">
                <label className="control-label">Byggnadstyper</label>
                <Select
                    closeMenuOnSelect={false}
                    isMulti
                    options={buildingTypeOptions}
                    value={selectedBuildingTypeOptions}
                    onChange={handleBuildingTypeChange}
                    placeholder="Välj byggnadstyper..."
                    noOptionsMessage={() => "Inga byggnadstyper tillgängliga"}
                    isClearable={false}
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            '&:hover': {
                                borderColor: '#999'
                            }
                        }),
                    }}
                />
            </div>

            <div className="control-group">
                <label htmlFor="group-select" className="control-label">Företag</label>
                <select
                    id="group-select"
                    value={selectedUserGroup || "all"}
                    onChange={handleUserGroupChange}
                    className="select-control"
                >
                    <option value="all">Alla företag</option>
                    {userGroups.map(group => (
                        <option key={group.key} value={group.key}>
                            {group.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="control-group">
                <label htmlFor="building-select" className="control-label">Byggnader</label>
                <Select<BuildingOption, true>
                    inputId="building-select"
                    closeMenuOnSelect={false}
                    isMulti
                    options={buildingOptions}
                    value={selectedBuildings}
                    onChange={(selected) => onSelectedBuildingKeysChange(selected.map(option => option.value))}
                    placeholder="Alla byggnader"
                    noOptionsMessage={() => "Inga byggnader"}
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            '&:hover': {
                                borderColor: '#999'
                            }
                        }),
                        // A picked building the other filters exclude: greyed, still removable
                        multiValue: (base, { data }) => data.matches ? base : { ...base, backgroundColor: '#f1f1f1', border: '1px dashed #bbb' },
                        multiValueLabel: (base, { data }) => data.matches ? base : { ...base, color: '#888', textDecoration: 'line-through' },
                    }}
                />
                {buildingNote && <p className="analysis-filter-note">{buildingNote}</p>}
            </div>

            {showTimeGrouping && (
                <TimeGroupingControl value={timeGrouping} onChange={onTimeGroupingChange} />
            )}

            {showDateRange && (<>
            <div className="control-group">
                <label htmlFor="date-start" className="control-label">Från datum</label>
                <input
                    id="date-start"
                    type="date"
                    name="start"
                    value={dateRange.start}
                    onChange={handleDateRangeChange}
                    className="date-control"
                />
            </div>

            <div className="control-group">
                <label htmlFor="date-end" className="control-label">Till datum</label>
                <input
                    id="date-end"
                    type="date"
                    name="end"
                    value={dateRange.end}
                    onChange={handleDateRangeChange}
                    className="date-control"
                />
            </div>
            </>)}

            {extraControls}
        </div>
    );
};

export default AnalysisControls;